import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Branching inline-SVG diagram of the contract lifecycle:
 * Active branches to Extended -> Redeemed (loan repaid) or to Expired -> Stock
 * (moved to INV/EXP). Theme-aware; labels are translated.
 */
const ContractLifecycleDiagram = () => {
    const theme = useTheme();
    const { t } = useTranslation();

    const nodeText = theme.palette.text.primary;
    const line = theme.palette.text.disabled;
    const surface = theme.palette.background.paper;

    const boxes: { x: number; y: number; label: string; color: string }[] = [
        { x: 16, y: 96, label: t('helpDiagrams.lifecycle.active'), color: theme.palette.primary.main },
        { x: 236, y: 20, label: t('helpDiagrams.lifecycle.extended'), color: theme.palette.info.main },
        { x: 452, y: 20, label: t('helpDiagrams.lifecycle.redeemed'), color: theme.palette.success.main },
        { x: 236, y: 172, label: t('helpDiagrams.lifecycle.expired'), color: theme.palette.warning.main },
        { x: 452, y: 172, label: t('helpDiagrams.lifecycle.stock'), color: theme.palette.error.main },
    ];

    const bw = 168;
    const bh = 44;

    return (
        <Box
            component="figure"
            aria-label={t('helpDiagrams.lifecycle.title')}
            sx={{ m: 0, my: 2, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}
        >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                {t('helpDiagrams.lifecycle.title')}
            </Typography>
            <Box
                component="svg"
                viewBox="0 0 640 240"
                sx={{ width: '100%', height: 'auto', maxWidth: 640 }}
            >
                <title>{t('helpDiagrams.lifecycle.title')}</title>
                <defs>
                    <marker
                        id="help-lc-arrow"
                        markerWidth="8"
                        markerHeight="8"
                        refX="6"
                        refY="3"
                        orient="auto"
                    >
                        <path d="M0,0 L6,3 L0,6 Z" fill={line} />
                    </marker>
                </defs>

                {/* Active -> Extended */}
                <path
                    d="M184,110 C210,110 210,42 232,42"
                    fill="none"
                    stroke={line}
                    strokeWidth={2}
                    markerEnd="url(#help-lc-arrow)"
                />
                {/* Active -> Expired */}
                <path
                    d="M184,118 C210,118 210,194 232,194"
                    fill="none"
                    stroke={line}
                    strokeWidth={2}
                    markerEnd="url(#help-lc-arrow)"
                />
                {/* Extended -> Redeemed */}
                <line
                    x1={404}
                    y1={42}
                    x2={448}
                    y2={42}
                    stroke={line}
                    strokeWidth={2}
                    markerEnd="url(#help-lc-arrow)"
                />
                {/* Expired -> Stock */}
                <line
                    x1={404}
                    y1={194}
                    x2={448}
                    y2={194}
                    stroke={line}
                    strokeWidth={2}
                    markerEnd="url(#help-lc-arrow)"
                />

                {boxes.map(box => (
                    <g key={box.label}>
                        <rect
                            x={box.x}
                            y={box.y}
                            width={bw}
                            height={bh}
                            rx={10}
                            fill={surface}
                            stroke={box.color}
                            strokeWidth={2}
                        />
                        <rect x={box.x} y={box.y} width={6} height={bh} rx={3} fill={box.color} />
                        <text
                            x={box.x + bw / 2 + 3}
                            y={box.y + bh / 2}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize={15}
                            fontWeight={600}
                            fill={nodeText}
                        >
                            {box.label}
                        </text>
                    </g>
                ))}
            </Box>
        </Box>
    );
};

export default ContractLifecycleDiagram;
